import {
   css,
   html,
   LitElement,
   PropertyDeclarations,
   PropertyValues,
} from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';

import * as Tags from './user-menus.tagnames';
import {
   AtlUserNotificationsMenuProps,
   AtlUserNotificationsToggleProps,
} from './user-menus.props';
import { BadgeEnums } from '../badge';
import { IconEnums } from '../icon';
import { BootstrapDropdown } from '../BootstrapDrowndown';
import {
   AtlUserNotificationsMenuCloseEvent,
   AtlUserNotificationsMenuToggleEvent,
   AtlUserProfileMenuToggleEvent,
} from './user-menus.events';
import { AtlIconClassFactory } from '../icon/icon.component';

const HEADER_OPTIONS_SLOT = 'PanelHeaderOptions';
const HEADER_TITLE_INDICATOR_SLOT = 'PanelHeaderTitleIndicator';

@customElement(Tags.UserNotificationsMenu)
export class AtlUserNotificationsMenu
   extends LitElement
   implements AtlUserNotificationsMenuProps
{
   declare panelHeaderTitle?: string;
   declare panelOptionsUsed?: boolean;

   declare toggleOptions?: AtlUserNotificationsToggleProps;

   private _bootStrapDropdown?: BootstrapDropdown;

   @query('button.btn')
   private _toggleButton?: HTMLButtonElement;

   @query('div.dropdown-menu')
   private _dropDownMenu?: HTMLDivElement;

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .notification-panel {
            margin-top: 15px !important;
         }
         slot[name='PanelBody'] {
            display: block;
            width: 100%;
         }
      `,
   ];

   static properties: PropertyDeclarations = {
      panelHeaderTitle: { type: String },
      panelOptionsUsed: { type: Boolean, state: true },
      toggleOptions: { type: Object },
   };

   constructor() {
      super();
      this.addEventListener(
         AtlUserNotificationsMenuCloseEvent.EVENT_NAME,
         () => {
            this._bootStrapDropdown?.hide();
         }
      );
   }

   override connectedCallback(): void {
      super.connectedCallback();
      window.addEventListener('click', this._handleWindowClick);
      window.addEventListener(
         AtlUserProfileMenuToggleEvent.EVENT_NAME,
         this._onUserProfileOpened
      );
   }

   override disconnectedCallback(): void {
      window.removeEventListener(
         AtlUserProfileMenuToggleEvent.EVENT_NAME,
         this._onUserProfileOpened
      );
      window.removeEventListener('click', this._handleWindowClick);
      super.disconnectedCallback();
   }

   private _onUserProfileOpened = () => {
      this._bootStrapDropdown?.hide();
   };

   private _onPanelOptionsSlotChange = () => {
      const slot = this.shadowRoot?.querySelector(
         `slot[name="${HEADER_OPTIONS_SLOT}"]`
      );

      const assignedNodes = (slot as HTMLSlotElement)?.assignedNodes({
         flatten: true,
      });
      this.panelOptionsUsed = assignedNodes?.length > 0;

      if (this.panelOptionsUsed) {
         const div = document.createElement('div');
         div.classList.add('header-options');

         assignedNodes?.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
               div.appendChild(node);
            }
         });
         slot?.parentNode!.replaceChild(div, slot);
      }
   };

   private get _badgeCount() {
      if (!this?.toggleOptions?.badgeCount) return '0';
      if (this.toggleOptions.badgeCount > 99) return '+99';
      return `${this.toggleOptions.badgeCount}`;
   }

   private get _badgeAs() {
      let badgeAsValue =
         this.toggleOptions?.badgeAs || BadgeEnums.BadgeAs.Normal;
      if (
         badgeAsValue !== BadgeEnums.BadgeAs.Dot &&
         !this.toggleOptions?.badgeShowZero &&
         !this.toggleOptions?.badgeCount
      ) {
         badgeAsValue = BadgeEnums.BadgeAs.Dot;
      }
      return badgeAsValue;
   }

   private _renderHeader = () => {
      if (!this.panelHeaderTitle && !this.panelOptionsUsed) {
         return html`
            <slot name="${HEADER_TITLE_INDICATOR_SLOT}"></slot>
            <slot
               name="${HEADER_OPTIONS_SLOT}"
               @slotchange=${this._onPanelOptionsSlotChange}
            ></slot>
         `;
      }

      return html`
         <div class="notification-panel-header">
            <div class="header-title-root">
               <div class="header-title">${this.panelHeaderTitle}</div>
               <slot name="${HEADER_TITLE_INDICATOR_SLOT}"></slot>
            </div>
            <slot
               name="${HEADER_OPTIONS_SLOT}"
               @slotchange=${this._onPanelOptionsSlotChange}
            ></slot>
         </div>
         <div class="notification-separator"></div>
      `;
   };

   private _renderIcon = () => {
      const iconClasses = AtlIconClassFactory({
         iconSource: IconEnums.IconSource.FontAwesome,
         iconName: 'bell',
      });
      iconClasses.push('menu-icon');
      return html`<i class=${iconClasses.join(' ')} aria-hidden="true"></i>`;
   };

   private _renderToggleContent = () => {
      if (this.toggleOptions?.badgeShow) {
         return html`<atlantis-badge
            variant=${BadgeEnums.Variant.Danger}
            as=${this._badgeAs}
            placement=${BadgeEnums.Placement.PositionAbsoluteTopRight}
            ?detached=${this._badgeAs !== BadgeEnums.BadgeAs.Dot}
            label=${this._badgeCount}
         >
            ${this._renderIcon()}
         </atlantis-badge>`;
      }
      return this._renderIcon();
   };

   protected override firstUpdated(changedProperties: PropertyValues): void {
      super.firstUpdated(changedProperties);

      this._bootStrapDropdown = bootstrap.Dropdown.getOrCreateInstance(
         this._toggleButton
      );
   }

   render() {
      return html`
         <div class="dropdown user-notification-menu">
            <button
               class="btn"
               type="button"
               data-bs-toggle="dropdown"
               data-bs-auto-close="outside"
               data-bs-display="static"
               aria-label="User Notifications"
               @click=${this._handleButtonClick}
            >
               ${this._renderToggleContent()}
            </button>
            <div
               class="dropdown-menu dropdown-menu-right notification-panel"
               tabindex="0"
            >
               ${this._renderHeader()}
               <div class="notification-panel-body component-custom-vertical-scrollbar">
                  <slot name="PanelBody"></slot>
               </div>
            </div>
         </div>
      `;
   }

   private _handleButtonClick = () => {
      this._bootStrapDropdown?.toggle();
      this.dispatchEvent(new AtlUserNotificationsMenuToggleEvent());
   };

   private _handleWindowClick = (event: MouseEvent) => {
      if (!this._dropDownMenu || !this._dropDownMenu.classList.contains('show'))
         return;

      const path = event.composedPath();

      const isButtonClicked =
         this._toggleButton && path.includes(this._toggleButton);
      if (isButtonClicked) return;

      const isClickInside =
         this._dropDownMenu && path.includes(this._dropDownMenu);
      if (!isClickInside) {
         this._bootStrapDropdown?.hide();
      }
   };
}
